import { useState } from "react";

import { Collapse, Color, HorizontalLine, Icon, IconName, Panel } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading, TextColor } from "capitalrx-components-2.0/lib/typography";

import { ServiceLineItem } from "@/App/api/claims";
import { useListProcedureCodesQuery } from "@/App/api/procedureCodes";

import { ClaimInfoStack } from "../Claims";
import { useTranslation } from "react-i18next";
import { formatDollars } from "capitalrx-utilities";

export const ClaimsDetailServicePanel = ({ service }: {service: ServiceLineItem}) => {
  const [ expanded, setExpanded ] = useState(false);
  const { t: translate } = useTranslation();
  const { data: procedureCodeData } = useListProcedureCodesQuery({ code: service.procedure_code }, !!service.procedure_code);
    const { stackColumns } = useResponsive({
      stackColumns: {
        desktop: 6,
        tablet: 12,
        mobile: 12,
      },
    });

  let displayedServiceCategory = service.service_category;

  if (service.preventive_category) {
    displayedServiceCategory = `${service.preventive_category} (Preventive)`;
  }

  const StatusMap = {
    paid: {
      icon: "check-circle",
      text: translate("CLAIMS.claimsDetailsPage.approved"),
      color: Color.Green[700],
    },
    denied: {
      icon: "x-circle",
      text:translate("CLAIMS.claimsDetailsPage.denied"),
      color: Color.Red[600],
    },
    pended: {
      text: translate("CLAIMS.claimsDetailsPage.inProgress"),
      color: Color.DarkGrey[400],
    },
    reversal: {
      icon: "info-circle",
      text: translate("CLAIMS.claimsDetailsPage.reversal"),
      color: Color.DarkGrey[400],
    },
  };

  return (
    <Panel appearance="primary" className="member-portal-panel">
      <Stack>
        {service?.status != "pended" && (
          <Shelf>
            <Subheading>{displayedServiceCategory}</Subheading>
            <Shelf.Spacer />
            <Shelf spacing="mini">
              <Icon color={StatusMap[service?.status]?.color} name={StatusMap[service?.status]?.icon as IconName} />
              <Semibold><TextColor appearance={StatusMap[service?.status]?.color}>{StatusMap[service?.status]?.text}</TextColor></Semibold>
            </Shelf>
          </Shelf>
        )}
        <Grid>
          {
            service?.status != "pended" &&
              <Grid.Span columns={stackColumns}>
                <ClaimInfoStack title={translate("CLAIMS.claimsDetailsPage.patientResponsibility")} body={formatDollars(service.patient_pay_amount)} />
              </Grid.Span>
          }
          {
            service.procedure_code && (
              <Grid.Span columns={6}>
                <ClaimInfoStack
                  title={translate("CLAIMS.claimsDetailsPage.procedureCode")}
                  body={
                    <Shelf alignment="center" spacing="mini">
                      {service.procedure_code}
                      {procedureCodeData?.results[0]?.procedure_code?.long_description
                        && <Icon name="info-circle" color="turquoise" tooltip={procedureCodeData?.results[0]?.procedure_code?.long_description} />
                      }
                    </Shelf>
                  } />
              </Grid.Span>
            )
          }
          {
            service?.status === "pended" &&
              <Grid.Span columns={6}>
                <Shelf>
                  <Shelf.Spacer />
                  <Semibold><TextColor appearance={StatusMap[service?.status]?.color}>{StatusMap[service?.status]?.text}</TextColor></Semibold>
                </Shelf>
              </Grid.Span>
          }
        </Grid>
        {
          service?.status != "pended" && (
            <Collapse.Blank.Unpadded className="member-portal-collapse" expanded={expanded} onToggleExpand={() => setExpanded((currentState) => !currentState)} title={expanded ? translate("CLAIMS.claimsDetailsPage.showLess") : translate("CLAIMS.claimsDetailsPage.showMore")}>
              <Stack>
                <Shelf><Body>{translate("CLAIMS.claimsDetailsPage.amountBilled")}</Body><Shelf.Spacer />{formatDollars(service?.line_item_charge_amount || "0")}</Shelf>
                {service?.status != "denied" &&
                  <Shelf>
                    <Body>{translate("CLAIMS.claimsDetailsPage.allowedByPlan")}</Body>
                    <Shelf.Spacer />
                    {formatDollars(service?.repriced_allowed_amount || "0")}
                  </Shelf>
                }
                {service?.status === "paid" &&
                  <Shelf>
                    <Body>{translate("CLAIMS.claimsDetailsPage.deductible")}</Body>
                    <Shelf.Spacer />
                    {formatDollars(service?.amount_applied_to_deductible || "0")}
                  </Shelf>
                }
                <HorizontalLine />
                <Shelf>
                  <Body>
                    {service?.status !== "denied" && translate("CLAIMS.claimsDetailsPage.planPays")}
                    {service?.status === "denied" && translate("CLAIMS.claimsDetailsPage.planPaid")}
                  </Body>
                  <Shelf.Spacer />{formatDollars(service?.plan_pay_amount || "0")}</Shelf>
                <HorizontalLine />
                <Shelf>
                  <Subheading>{translate("CLAIMS.claimsDetailsPage.patientResponsibility")}</Subheading>
                  <Shelf.Spacer />
                  <Subheading>{formatDollars(service?.patient_pay_amount || "0")}</Subheading>
                </Shelf>
              </Stack>
            </Collapse.Blank.Unpadded>
          )
        }
      </Stack>
    </Panel>
  );
};
